//libs
import React, { memo, useState, useEffect, useMemo, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';

//styles
import {
  Button,
  SlideContainer,
  TextFieldContainer,
  Range,
  Title,
  Error,
  InputContainer,
  InputElement,
  SliderTrack,
  TrackContainer,
  Text,
  LabelsContainer,
  PriceLabel,
} from './styles';

//helpers
import { getMinValue, getMaxValue } from './helpers';

//constants
import {
  TITLE,
  BTN_TEXT,
  DEBOUNCE_DELAY,
  MIN_AMOUNT,
  MAX_AMOUNT,
  SLIDER_STATES,
  MIN_ERROR_MSG,
  MAX_ERROR_MSG,
  INPUT_TYPES,
} from './constants';
import { DEFAULT_CURRENCY } from 'shared/constants';

// defs
interface IRangeProps {
  filter: Listing.IFilter;
  onApplyClick: (value: string) => void;
  appliedFilters: Listing.IAppliedFilters;
}

const RangeV2 = ({ filter, appliedFilters, onApplyClick }: IRangeProps) => {
  const min = getMinValue(filter);
  const max = getMaxValue(filter);

  const [minSelected, setMinSelected] = useState(min);
  const [maxSelected, setMaxSelected] = useState(max);
  const rangeRef = useRef({
    min: min,
    max: max,
  });

  const [whoIsActive, setWhoIsActive] = useState<number>(SLIDER_STATES.NONE);
  const [isInitialState, setIsInitialState] = useState(true);
  const [inFocus, setInFocus] = useState(INPUT_TYPES.NONE);

  useEffect(() => {
    const initialRangeFilters = appliedFilters || { min, max };
    if (isEmpty(initialRangeFilters)) return;
    setMinSelected(Number(initialRangeFilters.min));
    setMaxSelected(Number(initialRangeFilters.max));
    rangeRef.current.min = initialRangeFilters.min;
    rangeRef.current.max = initialRangeFilters.max;
  }, [appliedFilters, max, min]);

  const isMinError = useMemo(() => {
    if (minSelected < min || minSelected > max || minSelected > maxSelected) return true;
    return false;
  }, [max, maxSelected, min, minSelected]);

  const isMaxError = useMemo(() => {
    if (maxSelected > max || maxSelected < min || maxSelected < minSelected) return true;
    return false;
  }, [max, maxSelected, min, minSelected]);

  const leftPosition = useMemo(() => {
    //we have to check both the error states here for eror case as there are some cases on a change in a field, the other one becomes valid.
    const leftValue = parseInt(isMinError || isMaxError ? rangeRef.current.min : minSelected);
    if (leftValue < min) return 0;
    return ((leftValue - min) / (max - min)) * 100;
  }, [isMaxError, isMinError, max, min, minSelected]);

  const rightPosition = useMemo(() => {
    const rightValue = parseInt(isMinError || isMaxError ? rangeRef.current.max : maxSelected);
    if (rightValue > max) return 0;
    return ((max - rightValue) / (max - min)) * 100;
  }, [isMaxError, isMinError, max, maxSelected, min]);

  const validateInput = (val: number, isMin: boolean, selectedValue: number) => {
    //getting selectedvalue as an argument as we need to check the current value before setting it in state.
    if (isMin) return val <= selectedValue && val <= max && val >= min;
    return val >= selectedValue && val <= max && val >= min;
  };

  const debounceMin = debounce((val: number) => {
    if (validateInput(val, true, maxSelected)) {
      rangeRef.current.min = Number(val);
      if (validateInput(maxSelected, false, val)) rangeRef.current.max = maxSelected;
    }
    if (isInitialState) setIsInitialState(false);
    setInFocus(INPUT_TYPES.MIN);
    setMinSelected(Number(val));
  }, DEBOUNCE_DELAY);

  const debounceMax = debounce((val: number) => {
    if (validateInput(val, false, minSelected)) {
      rangeRef.current.min = minSelected;
      if (validateInput(minSelected, true, val)) rangeRef.current.max = Number(val);
    }
    if (isInitialState) setIsInitialState(false);
    setInFocus(INPUT_TYPES.MAX);
    setMaxSelected(Number(val));
  }, DEBOUNCE_DELAY);

  const setInitialValues = () => {
    if (isMinError) {
      rangeRef.current.min = min;
      setMinSelected(min);
    }
    if (isMaxError) {
      rangeRef.current.max = max;
      setMaxSelected(max);
    }
  };

  const setMinRangeValues = (val: number) => {
    setWhoIsActive(SLIDER_STATES.LEFT);
    const value = Math.min(val, rangeRef.current.max - 1);
    if (isInitialState) {
      setInitialValues();
      setIsInitialState(false);
    } else {
      rangeRef.current.min = value;
      setMinSelected(value);
    }
  };

  const setMaxRangeValues = (val: number) => {
    setWhoIsActive(SLIDER_STATES.RIGHT);
    const value = Math.max(val, rangeRef.current.min + 1);
    if (isInitialState) {
      setInitialValues();
      setIsInitialState(false);
    } else {
      rangeRef.current.max = value;
      setMaxSelected(value);
    }
  };

  const setRangeFilter = (value: number) => {
    const rightFilterDifference = Math.abs(value - rangeRef.current.max);
    const leftFilterDifference = Math.abs(value - rangeRef.current.min);
    if (whoIsActive === SLIDER_STATES.NONE) {
      //click
      if (rightFilterDifference > leftFilterDifference) {
        setMinRangeValues(value);
      } else {
        setMaxRangeValues(value);
      }
    } else {
      //drag
      if (whoIsActive === SLIDER_STATES.LEFT) {
        setMinRangeValues(value);
      } else {
        setMaxRangeValues(value);
      }
    }
  };

  const getErrorMessage = useMemo(() => {
    const minError = `${MIN_ERROR_MSG} ${min}`;
    const maxError = `${MAX_ERROR_MSG} ${max}`;
    if (isMaxError && isMinError) {
      if (inFocus === INPUT_TYPES.MIN) return minError;
      if (inFocus === INPUT_TYPES.MAX) return maxError;
    }
    if (isMinError) return minError;
    if (isMaxError) return maxError;
  }, [inFocus, isMaxError, isMinError, max, min]);

  const doApply = () => {
    onApplyClick(`${minSelected}-${maxSelected}`);
  };
  return (
    <div data-test="price-filter">
      <Title>{TITLE}</Title>
      <InputContainer data-test="price-input">
        <TextFieldContainer>
          <Text>{MIN_AMOUNT}</Text>
          <InputElement
            state={!isInitialState && isMinError ? 'error' : 'default'}
            type="number"
            variant="default"
            onChange={(e) => debounceMin(e.target.value)}
            value={minSelected}
            data-test="min-input"
          />
        </TextFieldContainer>
        <TextFieldContainer>
          <Text>{MAX_AMOUNT}</Text>
          <InputElement
            state={!isInitialState && isMaxError ? 'error' : 'default'}
            type="number"
            value={maxSelected}
            variant="default"
            onChange={(e) => debounceMax(e.target.value)}
            data-test="max-input"
          />
        </TextFieldContainer>
      </InputContainer>
      <Error>
        {!isInitialState && (isMaxError || isMinError) && (
          <span data-test="error-msg">{getErrorMessage}</span>
        )}
      </Error>
      <SlideContainer data-test="slider">
        <TrackContainer>
          <SliderTrack left={leftPosition} right={rightPosition}></SliderTrack>
        </TrackContainer>
        <Range
          type="range"
          min={min}
          max={max}
          value={rangeRef.current.min}
          onChange={(e) => setRangeFilter(Number(e.target.value))}
          isPriority={whoIsActive === SLIDER_STATES.LEFT}
          data-test="min-range"
          onMouseLeave={() => setWhoIsActive(SLIDER_STATES.NONE)}
          onMouseUp={() => setWhoIsActive(SLIDER_STATES.NONE)}
        />
        <Range
          type="range"
          min={min}
          max={max}
          value={rangeRef.current.max}
          onChange={(e) => setRangeFilter(Number(e.target.value))}
          isPriority={whoIsActive === SLIDER_STATES.RIGHT}
          data-test="max-range"
          onMouseLeave={() => setWhoIsActive(SLIDER_STATES.NONE)}
          onMouseUp={() => setWhoIsActive(SLIDER_STATES.NONE)}
        />
      </SlideContainer>
      <LabelsContainer>
        <PriceLabel>
          {DEFAULT_CURRENCY}
          {min.toLocaleString()}
        </PriceLabel>
        <PriceLabel>
          {DEFAULT_CURRENCY}
          {max.toLocaleString()}
        </PriceLabel>
      </LabelsContainer>
      <Button
        onClick={doApply}
        disabled={isMinError || isMaxError || (minSelected === min && maxSelected === max)}
        data-test="apply-btn"
      >
        {BTN_TEXT}
      </Button>
    </div>
  );
};

export default memo(RangeV2);
